import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeLatest } from 'redux-saga/effects';

import {
  apiLoadSubmitDialog,
  apiSaveDonationForm,
  apiSubmitCampaign,
  apiValidateCampaign,
  apiValidateCampaignPlusSubscription,
} from 'src/api/campaignsSetup';
import { SELF_ORIGIN } from 'src/config';
import { DonationPageSaveFormType } from 'src/models/DonationPage/Form';
import { DonationPageLoadSubmitDialogResponseType } from 'src/models/DonationPage/LoadSubmitDialog';
import {
  DonationPageFirstFormResponseType,
  DonationPageThirdFormResponseType,
  DonationPageSecondFormResponseType,
  DonationPageTabsEnum,
} from 'src/models/DonationPage/Section';
import { DonationPageSubmitCampaignRequestType } from 'src/models/DonationPage/SubmitCampaign';
import {
  loadDonationPageFirstFormData,
  loadDonationPageThirdFormData,
  LoadDonationPageCheckoutSectionData,
} from 'src/pages/DonationSetup/containers/loadDataFromResponse';
import { donationPageSlice } from 'src/pages/DonationSetup/containers/slice';
import { httpResponseCheckSaga } from 'src/store/commonSaga';
import { commonSlice } from 'src/store/commonPart/slice';
import { getCurrencySymbolById } from 'src/utils/currency';
import { hardRedirect } from 'src/utils/hardRedirect';

function* saveFormsSaga({ payload }: PayloadAction<DonationPageSaveFormType>): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiSaveDonationForm, payload);
    },
    function* (response) {
      if (payload.publish || payload.preview) {
        yield put(
          donationPageSlice.actions.validateStart({ id: payload.id, mode: payload.publish ? 'publish' : 'preview' })
        );
      }

      /** check what response has come and put the form data to store */
      if (payload.currentFormName !== response.CurrentSection.SectionName) {
        if (response.CurrentSection.SectionName === DonationPageTabsEnum.FIRST) {
          const { CurrentSection, CurrencyTypeID } = response as DonationPageFirstFormResponseType;

          yield put(
            donationPageSlice.actions.changeCurrentSectionData({
              currentSectionData: loadDonationPageFirstFormData(CurrentSection, CurrencyTypeID),
              activeFormName: DonationPageTabsEnum.FIRST,
            })
          );
        } else if (response.CurrentSection.SectionName === DonationPageTabsEnum.SECOND) {
          const { CurrentSection, CurrencyTypeID } = response as DonationPageSecondFormResponseType;

          donationPageSlice.actions.changeCurrentSectionData({
            currentSectionData: LoadDonationPageCheckoutSectionData(CurrentSection, CurrencyTypeID),
            activeFormName: DonationPageTabsEnum.SECOND,
          });
        } else if (response.CurrentSection.SectionName === DonationPageTabsEnum.THIRD) {
          const { CurrentSection, CurrencyTypeID } = response as DonationPageThirdFormResponseType;

          yield put(
            donationPageSlice.actions.changeCurrentSectionData({
              currentSectionData: loadDonationPageThirdFormData(CurrentSection, CurrencyTypeID),
              activeFormName: DonationPageTabsEnum.THIRD,
            })
          );
        }
      }

      const currencyType = getCurrencySymbolById(response.CurrencyTypeID) || '|';

      yield put(donationPageSlice.actions.saveFormOk({ ...response, currencyType }));
    },
    donationPageSlice.actions.saveFormError
  );
}

function* validateFormsSaga({ payload }: PayloadAction<{ id: string; mode: 'publish' | 'preview' }>): Generator {
  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiValidateCampaign, payload.id);
    },
    function* () {
      const {
        DesiredStartDateFormatted,
        Currencies,
        CurrencyTypeID,
        CanEditCurrency,
      }: DonationPageLoadSubmitDialogResponseType = yield call(apiLoadSubmitDialog, payload.id);

      const currencyOptions = Currencies
        ? Currencies.map((o) => ({
            label: o.Name,
            value: o.ID,
            symbol: o.Symbol.trim(),
          }))
        : [];

      /** check plusSubscription */
      let plusSubscriptionResponse = null;
      let plusSubscriptionErrors = false;

      yield httpResponseCheckSaga(
        function* () {
          return yield call(apiValidateCampaignPlusSubscription, payload.id);
        },
        // eslint-disable-next-line require-yield
        function* (response) {
          plusSubscriptionResponse = response;
        },
        function* (e) {
          plusSubscriptionErrors = true;
          yield put(donationPageSlice.actions.validateError(e));
        }
      );
      /** end check plusSubscription */

      if (!plusSubscriptionErrors) {
        yield put(
          donationPageSlice.actions.validateOk({
            date: DesiredStartDateFormatted?.length ? DesiredStartDateFormatted : null,
            canEditCurrency: CanEditCurrency,
            currencies: currencyOptions,
            currencySelected: CurrencyTypeID,
            mode: payload.mode,
            plusSubscription: plusSubscriptionResponse,
          })
        );
      }
    },
    donationPageSlice.actions.validateError
  );
}

function* publishSaga({ payload }: PayloadAction<DonationPageSubmitCampaignRequestType>): Generator {
  yield put(commonSlice.actions.toggleLoader(true));

  yield httpResponseCheckSaga(
    function* () {
      return yield call(apiSubmitCampaign, payload);
    },
    function* () {
      yield put(donationPageSlice.actions.publishOk());

      hardRedirect(`${SELF_ORIGIN}campaigns/autoapprove/${payload.PermanentUrl}`); // temporarily
      // yield call(customHistory.push, `${SELF_ORIGIN}campaigns/autoapprove/${payload}`);
    },
    donationPageSlice.actions.publishError,
    function* () {
      yield put(commonSlice.actions.toggleLoader(false));
    }
  );
}

export function* donationsPageSagas(): Generator {
  yield takeLatest(donationPageSlice.actions.saveFormStart.type, saveFormsSaga);

  yield takeLatest(donationPageSlice.actions.publishStart.type, publishSaga);

  yield takeLatest(donationPageSlice.actions.validateStart.type, validateFormsSaga);
}
